<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const { t } = useI18n();

const toneCard = {
  iris: 'border-n-iris-8 bg-n-iris-3/60 dark:bg-n-iris-4/30',
  blue: 'border-n-blue-9 bg-n-blue-3/70 dark:bg-n-blue-5/25',
  teal: 'border-n-teal-9 bg-n-teal-3/70 dark:bg-n-teal-5/25',
  amber: 'border-n-amber-9 bg-n-amber-3/70 dark:bg-n-amber-5/20',
  neutral: 'border-n-weak bg-n-solid-2',
};

const toneTitle = {
  iris: 'text-n-iris-11 dark:text-n-iris-11',
  blue: 'text-n-blue-11 dark:text-n-blue-11',
  teal: 'text-n-teal-11 dark:text-n-teal-11',
  amber: 'text-n-amber-11 dark:text-n-amber-11',
  neutral: 'text-n-slate-12',
};

const pipelineSteps = computed(() => [
  {
    key: 'webhook',
    icon: 'i-lucide-webhook',
    title: t('TAXIS.N8N_AUTOMATION.STEPS.WEBHOOK.TITLE'),
    body: t('TAXIS.N8N_AUTOMATION.STEPS.WEBHOOK.BODY'),
  },
  {
    key: 'normalize',
    icon: 'i-lucide-shrink',
    title: t('TAXIS.N8N_AUTOMATION.STEPS.NORMALIZE.TITLE'),
    body: t('TAXIS.N8N_AUTOMATION.STEPS.NORMALIZE.BODY'),
  },
  {
    key: 'identity',
    icon: 'i-lucide-database',
    title: t('TAXIS.N8N_AUTOMATION.STEPS.IDENTITY.TITLE'),
    body: t('TAXIS.N8N_AUTOMATION.STEPS.IDENTITY.BODY'),
  },
  {
    key: 'entry',
    icon: 'i-lucide-brain-circuit',
    title: t('TAXIS.N8N_AUTOMATION.STEPS.ENTRY.TITLE'),
    body: t('TAXIS.N8N_AUTOMATION.STEPS.ENTRY.BODY'),
  },
  {
    key: 'router',
    icon: 'i-lucide-git-branch',
    title: t('TAXIS.N8N_AUTOMATION.STEPS.ROUTER.TITLE'),
    body: t('TAXIS.N8N_AUTOMATION.STEPS.ROUTER.BODY'),
  },
]);

const routeBranches = computed(() => [
  {
    route: 'TAXIS_AI',
    tone: 'iris',
    bulletLines: [
      t('TAXIS.N8N_AUTOMATION.BRANCHES.TAXIS_AI.B1'),
      t('TAXIS.N8N_AUTOMATION.BRANCHES.TAXIS_AI.B2'),
      t('TAXIS.N8N_AUTOMATION.BRANCHES.TAXIS_AI.B3'),
    ],
  },
  {
    route: 'ATTRIBUTION',
    tone: 'blue',
    bulletLines: [
      t('TAXIS.N8N_AUTOMATION.BRANCHES.ATTRIBUTION.B1'),
      t('TAXIS.N8N_AUTOMATION.BRANCHES.ATTRIBUTION.B2'),
      t('TAXIS.N8N_AUTOMATION.BRANCHES.ATTRIBUTION.B3'),
    ],
  },
  {
    route: 'STATUS_DRIVER',
    tone: 'teal',
    bulletLines: [t('TAXIS.N8N_AUTOMATION.BRANCHES.STATUS_DRIVER.B1')],
  },
  {
    route: 'POSITION_UPDATE',
    tone: 'amber',
    bulletLines: [t('TAXIS.N8N_AUTOMATION.BRANCHES.POSITION_UPDATE.B1')],
  },
  {
    route: 'HUMAIN',
    tone: 'neutral',
    bulletLines: [t('TAXIS.N8N_AUTOMATION.BRANCHES.HUMAIN.B1')],
  },
  {
    route: 'FERMETURE',
    tone: 'neutral',
    bulletLines: [t('TAXIS.N8N_AUTOMATION.BRANCHES.FERMETURE.B1')],
  },
  {
    route: 'CLARIFICATION',
    tone: 'neutral',
    bulletLines: [t('TAXIS.N8N_AUTOMATION.BRANCHES.CLARIFICATION.B1')],
  },
]);

const dataStores = computed(() => [
  {
    key: 'pg',
    icon: 'i-lucide-server',
    label: t('TAXIS.N8N_AUTOMATION.LEGEND.PG_LABEL'),
    hint: t('TAXIS.N8N_AUTOMATION.LEGEND.PG_HINT'),
  },
  {
    key: 'wa',
    icon: 'i-lucide-message-circle',
    label: t('TAXIS.N8N_AUTOMATION.LEGEND.WA_LABEL'),
    hint: t('TAXIS.N8N_AUTOMATION.LEGEND.WA_HINT'),
  },
  {
    key: 'cw',
    icon: 'i-lucide-messages-square',
    label: t('TAXIS.N8N_AUTOMATION.LEGEND.CW_LABEL'),
    hint: t('TAXIS.N8N_AUTOMATION.LEGEND.CW_HINT'),
  },
  {
    key: 'llm',
    icon: 'i-lucide-sparkles',
    label: t('TAXIS.N8N_AUTOMATION.LEGEND.LLM_LABEL'),
    hint: t('TAXIS.N8N_AUTOMATION.LEGEND.LLM_HINT'),
  },
]);

const branchCardClass = tone => toneCard[tone] ?? toneCard.neutral;

const branchTitleClass = tone => toneTitle[tone] ?? toneTitle.neutral;

/** Étapes pour les flux demande de course / attribution (clés i18n résolues dans le script). */
const bookingLane = computed(() => [
  {
    title: t('TAXIS.N8N_AUTOMATION.SVG_BOOKING.L1'),
    sub: t('TAXIS.N8N_AUTOMATION.SVG_BOOKING.L1_SUB'),
  },
  {
    title: t('TAXIS.N8N_AUTOMATION.SVG_BOOKING.L2'),
    sub: t('TAXIS.N8N_AUTOMATION.SVG_BOOKING.L2_SUB'),
  },
  {
    title: t('TAXIS.N8N_AUTOMATION.SVG_BOOKING.L3'),
    sub: t('TAXIS.N8N_AUTOMATION.SVG_BOOKING.L3_SUB'),
  },
  {
    title: t('TAXIS.N8N_AUTOMATION.SVG_BOOKING.L4'),
    sub: t('TAXIS.N8N_AUTOMATION.SVG_BOOKING.L4_SUB'),
  },
  {
    title: t('TAXIS.N8N_AUTOMATION.SVG_BOOKING.L5'),
    sub: t('TAXIS.N8N_AUTOMATION.SVG_BOOKING.L5_SUB'),
  },
]);

const assignLane = computed(() => [
  {
    title: t('TAXIS.N8N_AUTOMATION.SVG_ASSIGN.A1'),
    sub: t('TAXIS.N8N_AUTOMATION.SVG_ASSIGN.A1_SUB'),
  },
  {
    title: t('TAXIS.N8N_AUTOMATION.SVG_ASSIGN.A2'),
    sub: t('TAXIS.N8N_AUTOMATION.SVG_ASSIGN.A2_SUB'),
  },
  {
    title: t('TAXIS.N8N_AUTOMATION.SVG_ASSIGN.A3'),
    sub: t('TAXIS.N8N_AUTOMATION.SVG_ASSIGN.A3_SUB'),
  },
  {
    title: t('TAXIS.N8N_AUTOMATION.SVG_ASSIGN.A4'),
    sub: t('TAXIS.N8N_AUTOMATION.SVG_ASSIGN.A4_SUB'),
  },
  {
    title: t('TAXIS.N8N_AUTOMATION.SVG_ASSIGN.A5'),
    sub: t('TAXIS.N8N_AUTOMATION.SVG_ASSIGN.A5_SUB'),
  },
]);
</script>

<template>
  <div class="flex flex-1 flex-col overflow-auto px-6 py-8">
    <div class="mx-auto flex w-full max-w-6xl flex-1 flex-col gap-10">
      <div>
        <div class="flex items-center gap-3">
          <div
            class="flex size-10 items-center justify-center rounded-xl bg-gradient-to-br from-n-iris-4/50 to-n-iris-5/40 ring-1 ring-n-iris-6/60"
          >
            <Icon icon="i-lucide-workflow" class="size-5 text-n-iris-11" />
          </div>
          <h1 class="text-xl font-semibold text-n-slate-12">
            {{ t('TAXIS.N8N_AUTOMATION.TITLE') }}
          </h1>
        </div>
        <p class="mt-2 max-w-3xl text-sm leading-relaxed text-n-slate-11">
          {{ t('TAXIS.N8N_AUTOMATION.SUBTITLE') }}
        </p>
      </div>

      <!-- Schéma principal -->
      <div
        class="rounded-2xl border border-n-weak bg-gradient-to-br from-n-solid-1 via-n-solid-1 to-n-alpha-2 p-6 shadow-sm md:p-10"
      >
        <p
          class="mb-6 text-xs font-semibold uppercase tracking-wider text-n-slate-10"
        >
          {{ t('TAXIS.N8N_AUTOMATION.DIAGRAM_CAPTION') }}
        </p>

        <div class="hidden md:block">
          <svg
            class="h-auto w-full text-n-slate-8"
            viewBox="0 0 920 200"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <defs>
              <marker
                id="mkr-pipe"
                :markerWidth="8"
                :markerHeight="8"
                :refX="7"
                :refY="4"
                orient="auto"
              >
                <path d="M0 0 L8 4 L0 8 Z" class="fill-current" />
              </marker>
            </defs>
            <path
              d="M40 100 H160 M200 100 H320 M360 100 H480 M520 100 H640 M680 100 H800"
              class="stroke-current"
              stroke-width="2"
              marker-end="url(#mkr-pipe)"
            />
            <rect
              x="10"
              y="60"
              width="120"
              height="80"
              rx="12"
              class="fill-n-solid-2 stroke-n-weak"
              stroke-width="1"
            />
            <text
              x="70"
              y="95"
              text-anchor="middle"
              class="fill-n-slate-12 text-[11px] font-semibold"
            >
              {{ t('TAXIS.N8N_AUTOMATION.SVG.WEBHOOK') }}
            </text>
            <text
              x="70"
              y="114"
              text-anchor="middle"
              class="fill-n-slate-11 text-[9px]"
            >
              {{ t('TAXIS.N8N_AUTOMATION.SVG.WEBHOOK_SUB') }}
            </text>
            <rect
              x="170"
              y="60"
              width="120"
              height="80"
              rx="12"
              class="fill-n-solid-2 stroke-n-weak"
              stroke-width="1"
            />
            <text
              x="230"
              y="95"
              text-anchor="middle"
              class="fill-n-slate-12 text-[11px] font-semibold"
            >
              {{ t('TAXIS.N8N_AUTOMATION.SVG.NORM') }}
            </text>
            <text
              x="230"
              y="114"
              text-anchor="middle"
              class="fill-n-slate-11 text-[9px]"
            >
              {{ t('TAXIS.N8N_AUTOMATION.SVG.NORM_SUB') }}
            </text>
            <rect
              x="330"
              y="60"
              width="120"
              height="80"
              rx="12"
              class="fill-n-iris-4/40 stroke-n-iris-8"
              stroke-width="1"
            />
            <text
              x="390"
              y="95"
              text-anchor="middle"
              class="fill-n-slate-12 text-[11px] font-semibold"
            >
              {{ t('TAXIS.N8N_AUTOMATION.SVG.ENTRY') }}
            </text>
            <text
              x="390"
              y="114"
              text-anchor="middle"
              class="fill-n-slate-11 text-[9px]"
            >
              {{ t('TAXIS.N8N_AUTOMATION.SVG.ENTRY_SUB') }}
            </text>
            <rect
              x="490"
              y="60"
              width="120"
              height="80"
              rx="12"
              class="fill-n-solid-2 stroke-n-weak"
              stroke-width="1"
            />
            <text
              x="550"
              y="95"
              text-anchor="middle"
              class="fill-n-slate-12 text-[11px] font-semibold"
            >
              {{ t('TAXIS.N8N_AUTOMATION.SVG.ROUTER') }}
            </text>
            <text
              x="550"
              y="114"
              text-anchor="middle"
              class="fill-n-slate-11 text-[9px]"
            >
              {{ t('TAXIS.N8N_AUTOMATION.SVG.ROUTER_SUB') }}
            </text>
            <rect
              x="650"
              y="42"
              width="260"
              height="116"
              rx="14"
              class="fill-n-teal-4/35 stroke-n-teal-9"
              stroke-width="1.5"
            />
            <text
              x="780"
              y="72"
              text-anchor="middle"
              class="fill-n-slate-12 text-xs font-semibold"
            >
              {{ t('TAXIS.N8N_AUTOMATION.SVG.OUTBOUND') }}
            </text>
            <text
              x="780"
              y="94"
              text-anchor="middle"
              class="fill-n-slate-11 text-[10px]"
            >
              {{ t('TAXIS.N8N_AUTOMATION.SVG.OUTBOUND_LINE2') }}
            </text>
            <text
              x="780"
              y="112"
              text-anchor="middle"
              class="fill-n-slate-11 text-[10px]"
            >
              {{ t('TAXIS.N8N_AUTOMATION.SVG.OUTBOUND_LINE3') }}
            </text>
          </svg>
        </div>

        <div class="flex flex-col gap-3 md:hidden">
          <div
            v-for="(step, idx) in pipelineSteps"
            :key="step.key"
            class="flex gap-3"
          >
            <div
              class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-n-solid-2 ring-1 ring-n-weak"
            >
              <Icon :icon="step.icon" class="size-5 text-n-slate-11" />
            </div>
            <div
              class="min-w-0 flex-1 rounded-xl border border-n-weak bg-n-solid-2 p-4"
            >
              <p
                class="text-xs font-semibold uppercase tracking-wide text-n-slate-10"
              >
                {{
                  t('TAXIS.N8N_AUTOMATION.MOBILE_STEP_TITLE', {
                    n: idx + 1,
                    title: step.title,
                  })
                }}
              </p>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ step.body }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <p class="text-center text-xs text-n-slate-11 md:hidden">
        {{ t('TAXIS.N8N_AUTOMATION.EXTRA_DIAGRAMS_HINT') }}
      </p>

      <!-- Répartition des 7 routes -->
      <div
        class="hidden rounded-2xl border border-n-weak bg-gradient-to-b from-n-alpha-2 to-n-solid-1 p-6 shadow-sm md:block md:p-8"
      >
        <p
          class="mb-4 text-xs font-semibold uppercase tracking-wider text-n-slate-10"
        >
          {{ t('TAXIS.N8N_AUTOMATION.DIAGRAM_FAN_CAPTION') }}
        </p>
        <svg
          class="h-auto w-full text-n-slate-8"
          viewBox="0 0 860 210"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <defs>
            <marker
              id="mkr-fan"
              :markerWidth="7"
              :markerHeight="7"
              :refX="6"
              :refY="3"
              orient="auto"
            >
              <path d="M0 0 L7 3 L0 7 Z" class="fill-current" />
            </marker>
          </defs>
          <rect
            x="320"
            y="6"
            width="210"
            height="48"
            rx="11"
            class="fill-n-iris-4/35 stroke-n-iris-9"
            stroke-width="1.5"
          />
          <text
            x="425"
            y="28"
            text-anchor="middle"
            class="fill-n-slate-12 text-[12px] font-semibold"
          >
            {{ t('TAXIS.N8N_AUTOMATION.SVG.ROUTER') }}
          </text>
          <text
            x="425"
            y="44"
            text-anchor="middle"
            class="fill-n-slate-11 text-[9px]"
          >
            {{ t('TAXIS.N8N_AUTOMATION.SVG.ROUTER_SUB') }}
          </text>
          <path
            d="M 425 54 L 425 66 M 99 66 L 795 66"
            class="stroke-current"
            stroke-width="1.5"
            marker-end="url(#mkr-fan)"
          />
          <path
            v-for="(cx, i) in [99, 215, 331, 447, 563, 679, 795]"
            :key="'stem-' + i"
            :d="'M ' + cx + ' 66 L ' + cx + ' 74'"
            class="stroke-n-slate-8"
            stroke-width="1.5"
          />
          <rect
            x="45"
            y="74"
            width="108"
            height="82"
            rx="10"
            class="fill-n-solid-2 stroke-n-weak"
            stroke-width="1"
          />
          <rect
            x="161"
            y="74"
            width="108"
            height="82"
            rx="10"
            class="fill-n-solid-2 stroke-n-weak"
            stroke-width="1"
          />
          <rect
            x="277"
            y="74"
            width="108"
            height="82"
            rx="10"
            class="fill-n-solid-2 stroke-n-weak"
            stroke-width="1"
          />
          <rect
            x="393"
            y="74"
            width="108"
            height="82"
            rx="10"
            class="fill-n-solid-2 stroke-n-weak"
            stroke-width="1"
          />
          <rect
            x="509"
            y="74"
            width="108"
            height="82"
            rx="10"
            class="fill-n-solid-2 stroke-n-weak"
            stroke-width="1"
          />
          <rect
            x="625"
            y="74"
            width="108"
            height="82"
            rx="10"
            class="fill-n-solid-2 stroke-n-weak"
            stroke-width="1"
          />
          <rect
            x="741"
            y="74"
            width="108"
            height="82"
            rx="10"
            class="fill-n-solid-2 stroke-n-weak"
            stroke-width="1"
          />
          <text
            x="99"
            y="98"
            text-anchor="middle"
            class="fill-n-slate-12 text-[9px] font-semibold"
          >
            {{ t('TAXIS.N8N_AUTOMATION.SVG_FAN.B1_TOP') }}
          </text>
          <text
            x="99"
            y="114"
            text-anchor="middle"
            class="fill-n-slate-11 text-[8px]"
          >
            {{ t('TAXIS.N8N_AUTOMATION.SVG_FAN.B1_SUB') }}
          </text>
          <text
            x="215"
            y="98"
            text-anchor="middle"
            class="fill-n-slate-12 text-[9px] font-semibold"
          >
            {{ t('TAXIS.N8N_AUTOMATION.SVG_FAN.B2_TOP') }}
          </text>
          <text
            x="215"
            y="114"
            text-anchor="middle"
            class="fill-n-slate-11 text-[8px]"
          >
            {{ t('TAXIS.N8N_AUTOMATION.SVG_FAN.B2_SUB') }}
          </text>
          <text
            x="331"
            y="98"
            text-anchor="middle"
            class="fill-n-slate-12 text-[9px] font-semibold"
          >
            {{ t('TAXIS.N8N_AUTOMATION.SVG_FAN.B3_TOP') }}
          </text>
          <text
            x="331"
            y="114"
            text-anchor="middle"
            class="fill-n-slate-11 text-[8px]"
          >
            {{ t('TAXIS.N8N_AUTOMATION.SVG_FAN.B3_SUB') }}
          </text>
          <text
            x="447"
            y="98"
            text-anchor="middle"
            class="fill-n-slate-12 text-[9px] font-semibold"
          >
            {{ t('TAXIS.N8N_AUTOMATION.SVG_FAN.B4_TOP') }}
          </text>
          <text
            x="447"
            y="114"
            text-anchor="middle"
            class="fill-n-slate-11 text-[8px]"
          >
            {{ t('TAXIS.N8N_AUTOMATION.SVG_FAN.B4_SUB') }}
          </text>
          <text
            x="563"
            y="98"
            text-anchor="middle"
            class="fill-n-slate-12 text-[9px] font-semibold"
          >
            {{ t('TAXIS.N8N_AUTOMATION.SVG_FAN.B5_TOP') }}
          </text>
          <text
            x="563"
            y="114"
            text-anchor="middle"
            class="fill-n-slate-11 text-[8px]"
          >
            {{ t('TAXIS.N8N_AUTOMATION.SVG_FAN.B5_SUB') }}
          </text>
          <text
            x="679"
            y="98"
            text-anchor="middle"
            class="fill-n-slate-12 text-[9px] font-semibold"
          >
            {{ t('TAXIS.N8N_AUTOMATION.SVG_FAN.B6_TOP') }}
          </text>
          <text
            x="679"
            y="114"
            text-anchor="middle"
            class="fill-n-slate-11 text-[8px]"
          >
            {{ t('TAXIS.N8N_AUTOMATION.SVG_FAN.B6_SUB') }}
          </text>
          <text
            x="795"
            y="98"
            text-anchor="middle"
            class="fill-n-slate-12 text-[9px] font-semibold"
          >
            {{ t('TAXIS.N8N_AUTOMATION.SVG_FAN.B7_TOP') }}
          </text>
          <text
            x="795"
            y="114"
            text-anchor="middle"
            class="fill-n-slate-11 text-[8px]"
          >
            {{ t('TAXIS.N8N_AUTOMATION.SVG_FAN.B7_SUB') }}
          </text>
        </svg>
      </div>

      <!-- Écosystème -->
      <div class="rounded-2xl border border-n-weak bg-n-solid-2 p-6 md:p-8">
        <p
          class="mb-6 text-xs font-semibold uppercase tracking-wider text-n-slate-10"
        >
          {{ t('TAXIS.N8N_AUTOMATION.DIAGRAM_ECOSYSTEM_CAPTION') }}
        </p>
        <div
          class="flex flex-col items-stretch gap-6 md:flex-row md:items-center md:justify-between"
        >
          <div
            class="flex-1 rounded-xl border border-n-weak bg-n-solid-1 p-5 ring-1 ring-n-alpha-2"
          >
            <p class="text-xs font-semibold uppercase text-n-slate-10">
              {{ t('TAXIS.N8N_AUTOMATION.SVG_ECO.SRC_TITLE') }}
            </p>
            <p class="mt-2 text-sm text-n-slate-12">
              {{ t('TAXIS.N8N_AUTOMATION.SVG_ECO.SRC_CW') }}
            </p>
          </div>
          <Icon
            icon="i-lucide-arrow-right"
            class="hidden size-6 shrink-0 text-n-slate-9 md:block"
          />
          <div
            class="flex-1 rounded-xl border-2 border-n-teal-8 bg-n-teal-3/25 p-5 dark:bg-n-teal-4/20"
          >
            <p class="text-xs font-semibold uppercase text-n-teal-11">
              {{ t('TAXIS.N8N_AUTOMATION.SVG_ECO.HUB_TITLE') }}
            </p>
            <p class="mt-2 text-sm text-n-slate-12">
              {{ t('TAXIS.N8N_AUTOMATION.SVG_ECO.HUB_SUB') }}
            </p>
          </div>
          <Icon
            icon="i-lucide-arrow-right"
            class="hidden size-6 shrink-0 text-n-slate-9 md:block"
          />
          <div
            class="flex flex-1 flex-col gap-3 rounded-xl border border-n-weak bg-n-solid-1 p-5 ring-1 ring-n-alpha-2"
          >
            <p class="text-xs font-semibold uppercase text-n-slate-10">
              {{ t('TAXIS.N8N_AUTOMATION.SVG_ECO.DST_TITLE') }}
            </p>
            <p class="flex items-start gap-2 text-sm text-n-slate-11">
              <Icon
                icon="i-lucide-phone"
                class="mt-0.5 size-4 shrink-0 text-n-ruby-10"
              />
              <span>{{ t('TAXIS.N8N_AUTOMATION.SVG_ECO.DST_WA') }}</span>
            </p>
            <p class="flex items-start gap-2 text-sm text-n-slate-11">
              <Icon
                icon="i-lucide-inbox"
                class="mt-0.5 size-4 shrink-0 text-n-ruby-10"
              />
              <span>{{ t('TAXIS.N8N_AUTOMATION.SVG_ECO.DST_CW') }}</span>
            </p>
            <p class="flex items-start gap-2 text-sm text-n-slate-11">
              <Icon
                icon="i-lucide-database"
                class="mt-0.5 size-4 shrink-0 text-n-ruby-10"
              />
              <span>{{ t('TAXIS.N8N_AUTOMATION.SVG_ECO.DST_PG') }}</span>
            </p>
          </div>
        </div>
      </div>

      <!-- Sous-parcours course + attribution -->
      <div class="grid grid-cols-1 gap-8 lg:grid-cols-2">
        <div
          class="rounded-2xl border border-n-weak bg-gradient-to-br from-n-solid-1 to-n-alpha-2 p-6 md:p-8"
        >
          <p
            class="mb-5 text-xs font-semibold uppercase tracking-wider text-n-slate-10"
          >
            {{ t('TAXIS.N8N_AUTOMATION.DIAGRAM_BOOKING_CAPTION') }}
          </p>
          <div
            class="flex flex-wrap items-center justify-center gap-x-1 gap-y-4 sm:gap-x-3"
          >
            <template v-for="(cell, ix) in bookingLane" :key="'bk-' + ix">
              <div
                class="min-h-[5.75rem] w-[31%] min-w-[104px] max-w-[148px] rounded-xl border border-n-weak bg-n-solid-2 px-3 py-3 text-center ring-1 ring-n-alpha-2"
              >
                <p class="text-xs font-semibold leading-tight text-n-slate-12">
                  {{ cell.title }}
                </p>
                <p class="mt-1 text-[10px] leading-snug text-n-slate-11">
                  {{ cell.sub }}
                </p>
              </div>
              <Icon
                v-if="ix < bookingLane.length - 1"
                icon="i-lucide-arrow-right"
                class="mx-1 hidden size-4 shrink-0 text-n-slate-9 xs:block sm:mx-0 sm:size-5 md:inline-block"
              />
            </template>
          </div>
        </div>
        <div
          class="rounded-2xl border border-n-weak bg-gradient-to-br from-n-solid-1 to-n-alpha-2 p-6 md:p-8"
        >
          <p
            class="mb-5 text-xs font-semibold uppercase tracking-wider text-n-slate-10"
          >
            {{ t('TAXIS.N8N_AUTOMATION.DIAGRAM_ASSIGN_CAPTION') }}
          </p>
          <div
            class="flex flex-wrap items-center justify-center gap-x-1 gap-y-4 sm:gap-x-3"
          >
            <template v-for="(cell, ix) in assignLane" :key="'as-' + ix">
              <div
                class="min-h-[5.75rem] w-[31%] min-w-[104px] max-w-[148px] rounded-xl border border-n-blue-8 bg-n-solid-2 px-3 py-3 text-center ring-1 ring-n-blue-4/30"
              >
                <p class="text-xs font-semibold leading-tight text-n-slate-12">
                  {{ cell.title }}
                </p>
                <p class="mt-1 text-[10px] leading-snug text-n-slate-11">
                  {{ cell.sub }}
                </p>
              </div>
              <Icon
                v-if="ix < assignLane.length - 1"
                icon="i-lucide-arrow-right"
                class="mx-1 hidden size-4 shrink-0 text-n-slate-9 xs:block sm:mx-0 sm:size-5 md:inline-block"
              />
            </template>
          </div>
        </div>
      </div>

      <div class="hidden md:grid md:grid-cols-5 md:gap-4">
        <div
          v-for="step in pipelineSteps"
          :key="'d-' + step.key"
          class="rounded-xl border border-n-weak bg-n-solid-2 p-4"
        >
          <Icon
            :icon="step.icon"
            class="mb-2 block size-5 text-n-ruby-9 dark:text-n-ruby-11"
          />
          <p class="text-sm font-medium text-n-slate-12">
            {{ step.title }}
          </p>
          <p class="mt-1 text-xs leading-snug text-n-slate-11">
            {{ step.body }}
          </p>
        </div>
      </div>

      <section>
        <h2 class="text-base font-semibold text-n-slate-12">
          {{ t('TAXIS.N8N_AUTOMATION.SECTION_BRANCHES') }}
        </h2>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ t('TAXIS.N8N_AUTOMATION.SECTION_BRANCHES_HINT') }}
        </p>
        <div class="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <article
            v-for="branch in routeBranches"
            :key="branch.route"
            class="rounded-xl border-2 p-4"
            :class="branchCardClass(branch.tone)"
          >
            <span
              class="font-mono text-xs font-bold"
              :class="branchTitleClass(branch.tone)"
            >
              {{ branch.route }}
            </span>
            <ul
              class="mt-3 list-inside list-disc space-y-1.5 text-sm text-n-slate-11"
            >
              <li v-for="(line, i) in branch.bulletLines" :key="i">
                {{ line }}
              </li>
            </ul>
          </article>
        </div>
      </section>

      <section class="rounded-2xl border border-n-weak bg-n-solid-2 p-6">
        <h2 class="text-base font-semibold text-n-slate-12">
          {{ t('TAXIS.N8N_AUTOMATION.SECTION_LEGEND') }}
        </h2>
        <div class="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div v-for="item in dataStores" :key="item.key" class="flex gap-3">
            <Icon
              :icon="item.icon"
              class="mt-0.5 size-5 shrink-0 text-n-ruby-9 dark:text-n-ruby-11"
            />
            <div>
              <p class="text-sm font-medium text-n-slate-12">
                {{ item.label }}
              </p>
              <p class="mt-0.5 text-xs text-n-slate-11">
                {{ item.hint }}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>
